export type GaiaObjectType = "audit" | "notice" | "litigation";

export type ClosingRequirementSeverity = "BLOCK" | "WARNING";

export type ClosingRequirementType =
  | "FILE"
  | "AMOUNT"
  | "TEXT"
  | "LINK"
  | "ATTESTATION"
  | "DATE"
  | "ALL_TASKS_CLOSED"
  | "CLOSE_DATE_SET"
  | "OUTCOME_PRESENT"
  | "ASSESSMENT_LINKED"
  | "PAYMENT_LINKED";

export type ClosingRequirementLinkTarget =
  | "ANY"
  | "EXTERNAL_URL"
  | "GAIA_OBJECT";

export interface ClosingRequirement {
  requirementId: string;
  requirementType: ClosingRequirementType;
  title: string;
  description?: string;
  severity: ClosingRequirementSeverity;
  active: boolean;
  isDefault: boolean;
  attributes?: Record<string, string>;
}

export interface ClosingRequirementValue {
  requirementId: string;
  documentIds?: string[];
  amount?: string;
  text?: string;
  link?: string;
  confirmed?: boolean;
  date?: number;
}

export interface ClosingRequirementResult {
  requirementId: string;
  requirementType: ClosingRequirementType;
  title: string;
  severity: ClosingRequirementSeverity;
  met: boolean;
  reason?: string;
}

export interface EvaluateClosingRequirementsRequest {
  gaiaObjectId: string;
  closeDate?: number;
  closingRequirementValues: ClosingRequirementValue[];
}

export interface EvaluateClosingRequirementsResponse {
  results: ClosingRequirementResult[];
}

export interface ClosingRequirementsConfig {
  teamName: string;
  gaiaObjectType: GaiaObjectType;
  requirements: ClosingRequirement[];
  modifiedAt?: string;
  modifiedBy?: string;
}

export interface GetClosingRequirementsRequest {
  teamName: string;
}

export interface GetClosingRequirementsResponse {
  configs: ClosingRequirementsConfig[];
}

export interface ClosingRequirementInput {
  requirementId?: string;
  requirementType: ClosingRequirementType;
  title: string;
  description?: string;
  severity: ClosingRequirementSeverity;
  active: boolean;
  attributes?: Record<string, string>;
}

export interface UpdateClosingRequirementsRequest {
  teamName: string;
  gaiaObjectType: GaiaObjectType;
  defaultRules: Record<string, boolean>;
  customRequirements: ClosingRequirementInput[];
  expectedModifiedAt?: string;
}

export interface UpdateClosingRequirementsResponse {
  config: ClosingRequirementsConfig;
}

export const GAIA_OBJECT_TYPES: {
  type: GaiaObjectType;
  feature: string;
  label: string;
}[] = [
  { type: "audit", feature: "AUDIT", label: "Audit" },
  { type: "notice", feature: "NOTICE", label: "Notice" },
  { type: "litigation", feature: "LITIGATION", label: "Litigation" },
];

export const VALUE_REQUIREMENT_TYPES: ClosingRequirementType[] = [
  "FILE",
  "AMOUNT",
  "TEXT",
  "LINK",
  "ATTESTATION",
  "DATE",
];

export const DOMAIN_CHECK_REQUIREMENT_TYPES: ClosingRequirementType[] = [
  "ALL_TASKS_CLOSED",
  "CLOSE_DATE_SET",
  "OUTCOME_PRESENT",
  "ASSESSMENT_LINKED",
  "PAYMENT_LINKED",
];

export const OUTCOME_REQUIREMENT_TYPES: ClosingRequirementType[] = [
  "OUTCOME_PRESENT",
  "ASSESSMENT_LINKED",
  "PAYMENT_LINKED",
];

export const REQUIREMENT_TYPE_LABELS: Record<ClosingRequirementType, string> = {
  FILE: "File is selected",
  AMOUNT: "Amount is entered",
  TEXT: "Text is entered",
  LINK: "Link is provided",
  ATTESTATION: "Attestation is confirmed",
  DATE: "Date is set",
  ALL_TASKS_CLOSED: "All tasks are closed",
  CLOSE_DATE_SET: "Close date is set",
  OUTCOME_PRESENT: "Outcome is present (payment or final assessment)",
  ASSESSMENT_LINKED: "An assessment is linked",
  PAYMENT_LINKED: "A payment is linked",
};

export const SEVERITY_COLUMN_LABELS: Record<
  ClosingRequirementSeverity,
  string
> = {
  BLOCK: "Block the close",
  WARNING: "Warn only",
};

export const SEVERITY_OPTIONS: {
  value: ClosingRequirementSeverity;
  label: string;
  description: string;
}[] = [
  {
    value: "BLOCK",
    label: "Block",
    description: "Close is not allowed until this is met.",
  },
  {
    value: "WARNING",
    label: "Warning",
    description: "The user is warned but can still close.",
  },
];

export const LINK_TARGET_OPTIONS: {
  value: ClosingRequirementLinkTarget;
  label: string;
}[] = [
  { value: "ANY", label: "Any" },
  { value: "EXTERNAL_URL", label: "External URL only" },
  { value: "GAIA_OBJECT", label: "Gaia object only" },
];

export type ClosingRequirementChange =
  | { kind: "toggleDefault"; requirementId: string; active: boolean }
  | { kind: "toggleCustom"; requirementId: string; active: boolean }
  | { kind: "add"; requirement: ClosingRequirementInput }
  | { kind: "edit"; requirement: ClosingRequirementInput }
  | { kind: "delete"; requirementId: string };

const toInput = ({
  isDefault: _isDefault,
  ...requirement
}: ClosingRequirement): ClosingRequirementInput => requirement;

export const toUpdateRequest = (
  config: ClosingRequirementsConfig,
  change: ClosingRequirementChange,
): UpdateClosingRequirementsRequest => {
  const defaultRules = Object.fromEntries(
    config.requirements
      .filter((r) => r.isDefault)
      .map((r) => [r.requirementId, r.active]),
  );
  let customRequirements = config.requirements
    .filter((r) => !r.isDefault)
    .map(toInput);
  switch (change.kind) {
    case "toggleDefault":
      defaultRules[change.requirementId] = change.active;
      break;
    case "toggleCustom":
      customRequirements = customRequirements.map((r) =>
        r.requirementId === change.requirementId
          ? { ...r, active: change.active }
          : r,
      );
      break;
    case "add":
      customRequirements = [...customRequirements, change.requirement];
      break;
    case "edit":
      customRequirements = customRequirements.map((r) =>
        r.requirementId === change.requirement.requirementId
          ? change.requirement
          : r,
      );
      break;
    case "delete":
      customRequirements = customRequirements.filter(
        (r) => r.requirementId !== change.requirementId,
      );
      break;
  }
  return {
    teamName: config.teamName,
    gaiaObjectType: config.gaiaObjectType,
    defaultRules,
    customRequirements,
    expectedModifiedAt: config.modifiedAt,
  };
};

// A domain check counts as configured whether its default is on or off.
export const availableRequirementTypes = (
  config: ClosingRequirementsConfig | undefined,
  currentType?: ClosingRequirementType,
): ClosingRequirementType[] => {
  const configured = new Set(
    config?.requirements.map((r) => r.requirementType) ?? [],
  );
  return [
    ...VALUE_REQUIREMENT_TYPES,
    ...DOMAIN_CHECK_REQUIREMENT_TYPES.filter(
      (type) => type === currentType || !configured.has(type),
    ),
  ];
};
